/**
 * ThreatGauge Component
 * 
 * Radial gauge for displaying threat/probability levels.
 */

import React from 'react';

export default function ThreatGauge({ value = 0, size = 200 }) {
  // Clamp value between 0 and 1
  const normalizedValue = Math.max(0, Math.min(1, value));
  
  // Calculate arc
  const radius = 80;
  const strokeWidth = 12;
  const circumference = Math.PI * radius; // Half circle
  const offset = circumference - (normalizedValue * circumference);
  
  // Color based on value
  const getColor = () => {
    if (normalizedValue > 0.7) return '#ef4444'; // red
    if (normalizedValue > 0.5) return '#f97316'; // orange
    if (normalizedValue > 0.3) return '#eab308'; // yellow
    return '#22c55e'; // green
  };

  const getLabel = () => {
    if (normalizedValue > 0.7) return 'CRITICAL';
    if (normalizedValue > 0.5) return 'HIGH';
    if (normalizedValue > 0.3) return 'ELEVATED';
    return 'LOW';
  };

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size / 2 + 30} viewBox="0 0 200 130">
        {/* Background arc */}
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke="#334155"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Value arc */}
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: 'stroke-dashoffset 0.5s ease-in-out, stroke 0.3s ease'
          }}
        />
        
        {/* Tick marks */}
        {[0, 0.25, 0.5, 0.75, 1].map((tick, i) => {
          const angle = Math.PI - (tick * Math.PI);
          const x1 = 100 + (radius - 20) * Math.cos(angle);
          const y1 = 100 - (radius - 20) * Math.sin(angle);
          const x2 = 100 + (radius - 8) * Math.cos(angle);
          const y2 = 100 - (radius - 8) * Math.sin(angle);
          
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#64748b"
              strokeWidth={2}
            />
          );
        })}
        
        {/* Needle */}
        <g transform={`rotate(${180 - normalizedValue * 180}, 100, 100)`}>
          <line
            x1="100"
            y1="100"
            x2="100"
            y2="30"
            stroke="#fff"
            strokeWidth={3}
            strokeLinecap="round"
          />
          <circle cx="100" cy="100" r="8" fill="#1e293b" stroke="#fff" strokeWidth={2} />
        </g>
        
        {/* Labels */}
        <text x="20" y="120" fill="#64748b" fontSize="10" textAnchor="middle">0</text>
        <text x="180" y="120" fill="#64748b" fontSize="10" textAnchor="middle">100</text>
      </svg>
      
      {/* Value display */}
      <div className="text-center -mt-4">
        <div className="text-3xl font-bold" style={{ color: getColor() }}>
          {(normalizedValue * 100).toFixed(0)}%
        </div>
        <div className="text-sm text-slate-400">{getLabel()}</div>
      </div>
    </div>
  );
}
